/** NOTE
 * The function processFooterData() in /lib/modules/normerende creates a ready-to-use object
 * that is intended to be passed directly into this component and work.
*/

import React, { useState } from "react";
import { Button } from "@hdir/ui/button";
import {
  Accordion, AccordionItem, AccordionContent, AccordionTrigger
} from "@hdir/ui/accordion";
import { faPrint } from "@fortawesome/pro-light-svg-icons";
import { ContentFooterPropsTypeWrapped } from "./types.d";
import FontAwesomeIconWrapper from "../FontAwesomeIconWrapper/FontAwesomeIconWrapper";
import ContentFooterMetadata from "../ContentFooterMetadata/ContentFooterMetadata";
import { resolveServiceUrl } from "../Pdf/resolveServiceUrl";
import { requestPdf } from "../../../assets/js/project/requestPdf";

function b64toBlob(b64Data: string, contentType: string) {
  const sliceSize = 512;
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}

function ContentFooter(props: Readonly<ContentFooterPropsTypeWrapped>) {
  const {
    data: {
      contributorRole,
      contributors,
      firstPublish,
      lastChange,
      versionPageUrl,
      getPdfService,
      contentUrl,
      contentTitle,
      refText,
      apiData,
      phrases
    }
  } = props;

  const metadata = {
    contributorRole,
    contributors,
    firstPublish,
    lastChange,
    versionPageUrl,
    phrases,
    usedInContentFooter: true
  };

  const [loading, setLoading] = useState(false);

  const handlePdf = () => {
    if (loading) return;
    const resolvedGetPdfService = resolveServiceUrl(getPdfService, window?.location?.pathname || "");

    setLoading(true);

    requestPdf(resolvedGetPdfService, contentUrl, JSON.stringify({ goalTitle: contentTitle }))
      .then((data) => {
        const fileName = `${contentTitle}.pdf`;
        const blob = b64toBlob(data.content.content, "application/pdf");

        if (window.navigator.msSaveBlob) { // download method for IE
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else { // download method for all other browsers
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }

        return data;
      })
      .catch((error) => {
        console.error("Failed to generate PDF", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="flex flex-col">
      <div className="contentFooter mt-xxl mb-xxl self-center w-full">
        <ContentFooterMetadata data={metadata} />
        <Button
          className="!pl-0 !pr-0 !pb-xxs mt-md !rounded-none contentFooter-border-bottom"
          data-noindex="true"
          aria-busy={loading}
          disabled={loading}
          isLoading={loading}
          loadingText={phrases["contentFooter.loadingText"]}
          onClick={handlePdf}
          variant="tertiary"
        >
          {phrases["contentFooter.pdf"]}
          <FontAwesomeIconWrapper icon={faPrint} style={{ color: "var(--design-color-icon-action-default)" }} />
        </Button>

        <Accordion className="max-w-2xl mt-md" data-noindex="true">
          <AccordionItem className="!rounded-t-sm !rounded-b-sm">
            <AccordionTrigger headingLevel="h2">
              <span className="text-md">
                {phrases["contentFooter.reference"]}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {refText}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Accordion className="max-w-2xl mt-md" data-noindex="true">
          <AccordionItem className="!rounded-sm">
            <AccordionTrigger headingLevel="h2">
              <span className="text-md">
                {phrases["contentFooter.api"]}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {apiData.text}
              <a href={apiData.linkUrl}>
                {apiData.linkUrlText}
              </a>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

export default function(props: Readonly<ContentFooterPropsTypeWrapped>) {
  return <ContentFooter {...props} />;
}
